import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import config from '@config'
import translate from '@i18n'
import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { StoryAPIProps, ToplistAPIProps } from '@widgets/Toplist'
import SvgIcon from '@components/SvgIcon'
import Story from '@components/Toplist/Story'
import Wrapper from '@components/Toplist/Wrapper'

type MostCommentedProps = Pick<ToplistAPIProps, 'title' | 'hideOnDesktop'>

type MostCommentedStoryProps = StoryAPIProps & {
  order: number
}

const {
  recommendation: { apiUrl: recommendationsApiUrl },
} = config

const getMostCommented = async () => {
  let responseData = []

  try {
    responseData = (
      await (
        await fetch(`${recommendationsApiUrl}/most-commented`, {
          credentials: 'include',
        })
      ).json()
    ).data
      ?.filter?.((story: any) => Object.keys(story).length > 0)
      ?.slice(0, 3)
  } catch {}

  return responseData
}

const isValidStories = (stories?: any[]): stories is StoryAPIProps[] =>
  (stories as any[])?.filter?.((story: any) => Object.keys(story).length > 0)
    ?.length > 2

const CommentCountWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing4 },
    },
  }) => css`
    display: flex;
    align-items: center;
    margin-top: ${spacing4};
  `}
`

const CommentCountIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing4 },
    },
  }) => css`
    margin-right: ${spacing4};
  `}
`

const CommentCountText = styled.span`
  ${({
    theme: {
      typography: {
        catchword: {
          medium1: { bundledCSS: catchwordMedium1CSS },
        },
      },
      colors: { textDefault },
    },
  }) => css`
    ${catchwordMedium1CSS};
    color: ${textDefault};
    text-decoration: none;
  `}
`

const MostCommentedStory: FunctionComponent<MostCommentedStoryProps> = ({
  articleId,
  title,
  catchword,
  link,
  isPlus,
  aureusOfferId,
  commentCount,
  order,
}) => {
  const onClick = useCallback(
    () => ({
      event: 'element_click',
      element: 'most-commented',
      link_url: link?.href,
    }),
    [link?.href],
  )

  const trackOnClick = useTracking(onClick)

  return (
    <Story
      key={articleId}
      order={order}
      title={title}
      catchword={catchword}
      articleId={articleId}
      link={link}
      isPlus={isPlus}
      aureusOfferId={aureusOfferId}
      onClick={trackOnClick}
    >
      <CommentCountWrapper>
        <CommentCountIcon size={24} iconName="message-lines" />
        <CommentCountText>
          {commentCount} {translate('topList.commentCount')}
        </CommentCountText>
      </CommentCountWrapper>
    </Story>
  )
}

const MostCommented: FunctionComponent<MostCommentedProps> = ({
  title,
  hideOnDesktop,
}) => {
  const pageMetadata = usePageMetadata()

  const id = (pageMetadata as PageMetadata)?.id

  const { data: apiToplistData } = useQuery<StoryAPIProps[]>({
    queryKey: ['most-commented', id],
    queryFn: getMostCommented,
    gcTime: 0,
  })

  const finalStories = useMemo(
    () =>
      isValidStories(apiToplistData) ? (
        <>
          {apiToplistData.map((story, index) => (
            <MostCommentedStory key={index} {...story} order={index + 1} />
          ))}
        </>
      ) : null,
    [apiToplistData],
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'element_impression',
      element: 'most-commented',
    }),
    [],
  )

  const onImpression = useTracking(onImpressionTracking)

  return (
    <Wrapper
      title={title}
      hideOnDesktop={hideOnDesktop}
      onImpression={onImpression}
    >
      {finalStories}
    </Wrapper>
  )
}

export default MostCommented
